/**
 * Variant Card Renderer for LGN
 * Generates HTML for variant cards matching product-card.liquid structure
 */

class VariantCard {
  constructor(options = {}) {
    this.imageRatio = options.imageRatio || 'square';
    this.showVendor = options.showVendor !== false;
    this.showPrice = options.showPrice !== false;
    this.showSize = options.showSize !== false;
    this.showCartBtn = options.showCartBtn !== false;
    this.cartDrawerEnabled = options.cartDrawerEnabled !== false;
  }

  /**
   * Render a single variant card
   * @param {Object} variant - Flattened variant object from StorefrontAPI
   * @returns {string} HTML string
   */
  render(variant) {
    const root = window.Shopify?.routes?.root || '/';
    const productUrl = `${root}products/${variant.productHandle}?variant=${this.extractVariantId(variant.variantId)}`;
    //const productUrl = `/products/${variant.productHandle}?variant=${this.extractVariantId(variant.variantId)}`;
    const imageUrl = variant.variantImage || variant.productImage;
    const imageAlt = variant.variantImageAlt || variant.productTitle;

    return `
      <div class="product-card p-3" data-variant-id="${variant.variantId}">
        <div class="product-card__image-wrapper position-relative mb-3" data-image-ratio="${this.imageRatio}">
          <a href="${productUrl}" aria-label="${this.escapeHtml(variant.productTitle)}" title="${this.escapeHtml(variant.productTitle)}" tabindex="-1">
            ${this.renderImage(imageUrl, imageAlt)}
          </a>
          ${this.renderBadge(variant)}
          ${this.showCartBtn && variant.available && !variant.surDemande ? this.renderCartButton(variant) : ''}
        </div>

        <div class="product-card__content-wrapper py-3">
          ${this.showVendor ? `<span class="vendor">${this.escapeHtml(variant.vendor)}</span>` : ''}

          <a href="${productUrl}" title="${this.escapeHtml(variant.productTitle)}" class="tab-able-item">
            <h5 class="product-title">${this.escapeHtml(variant.productTitle)}</h5>
          </a>

          ${this.showSize ? this.renderVariantInfo(variant) : ''}

          ${this.showPrice ? this.renderPrice(variant) : ''}
          ${variant.surDemande ? this.renderContactLink() : ''}
        </div>
      </div>
    `;
  }

  /**
   * Render variant image
   */
  renderImage(imageUrl, imageAlt) {
    if (!imageUrl) {
      return `
        <div class="product-card__image w-100 h-100 position-absolute left-0 top-0 placeholder">
          <svg class="object-cover w-100 h-100" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
            <rect fill="#f5f5f5" width="100" height="100"/>
            <text x="50" y="50" text-anchor="middle" dy=".3em" fill="#999" font-size="10">No image</text>
          </svg>
        </div>
      `;
    }

    // Add size parameter for responsive images
    const optimizedUrl = this.optimizeImageUrl(imageUrl, 1000);

    return `
      <div class="product-card__image img-hover-zoom w-100 h-100 position-absolute left-0 top-0 img-blur">
        <img
          src="${optimizedUrl}"
          alt="${this.escapeHtml(imageAlt)}"
          loading="lazy"
          class="object-cover w-100 h-100"
          sizes="(min-width: 992px) 33vw, 100vw"
        />
      </div>
    `;
  }

  /**
   * Render add to cart button
   */
  renderCartButton(variant) {
    const numericVariantId = this.extractVariantId(variant.variantId);

    return `
      <variant-cart-form
        class="btn-cart-wrapper position-absolute z-index-1 d-flex justify-content-center align-items-center"
        data-variant-id="${numericVariantId}"
      >
        <form>
          <input type="hidden" name="id" value="${numericVariantId}" />
          <input type="hidden" name="quantity" value="1" />
        </form>
        <button
          type="button"
          class="btn-cart cursor-pointer d-flex justify-content-center align-items-center tab-able-item"
          data-disabled="false"
          aria-label="Ajouter au panier"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="24" height="24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        </button>
      </variant-cart-form>
    `;
  }

  /**
   * Render product badge if applicable
   * @param {Object} variant - Variant object with productTags array
   */
  renderBadge(variant) {
    if (!variant.productTags || !Array.isArray(variant.productTags)) {
      return '';
    }

    let badgeImage = null;
    let tooltipText = null;

    // Priority: rare > selection
    if (
      variant.productTags.includes('rare') ||
      variant.productTags.includes('vin-rare') ||
      variant.productTags.includes('Vin rare')
    ) {
      badgeImage = 'badge-rare.svg';
      tooltipText = 'Vin rare';
    } else if (variant.productTags.includes('selection')) {
      badgeImage = 'badge-selection.svg';
      tooltipText = 'Sélection Les Grands Nectars';
    }

    if (!badgeImage) return '';

    // Use Shopify asset_url helper via global
    const assetUrl = window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cdn/shop/files/${badgeImage}`
      : `/assets/${badgeImage}`;

    return `
      <img
        src="${assetUrl}"
        class="lgn-product-badge lgn-product-badge--default"
        alt="${this.escapeHtml(tooltipText)}"
        data-bs-toggle="tooltip"
        data-bs-placement="auto"
        title="${this.escapeHtml(tooltipText)}"
      />
    `;
  }

  /**
   * Render variant info (size + packaging)
   */
  renderVariantInfo(variant) {
    const parts = [];

    // Size (e.g., "75cl", "150cl")
    if (variant.size) {
      parts.push(variant.size);
    }

    // Conditioning (e.g., "CBO3", "CBO6") — hide UNIT since it adds no info
    if (variant.packaging && variant.packaging !== 'UNIT') {
      parts.push(variant.packaging);
    }

    // Fallback to variant title if no parsed options
    const displayText = parts.length > 0 ? parts.join(' ') : variant.variantTitle;

    return `<div class="product-volume">${this.escapeHtml(displayText)}</div>`;
  }

  /**
   * Render price in French format
   */
  renderPrice(variant) {
    // Don't render price for sur_demande variants
    if (variant.surDemande) {
      return '';
    }

    const formattedPrice = this.formatPriceFrench(variant.price, 'HT');
    const priceTtc = Math.round(variant.price * 1.2 * 100) / 100;
    const formattedTtc = this.formatPriceFrench(priceTtc, 'TTC');

    let html = `<div class="product-price">${formattedPrice}</div><div class="product-price product-price--ttc">${formattedTtc}</div>`;

    // Add compare-at price if on sale
    if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
      const formattedCompareAt = this.formatPriceFrench(variant.compareAtPrice, 'HT');
      html = `
        <div class="product-price">
          <span class="product-price--sale">${formattedPrice}</span>
          <span class="product-price--compare-at">${formattedCompareAt}</span>
        </div>
        <div class="product-price product-price--ttc">${formattedTtc}</div>
      `;
    }

    return html;
  }

  /**
   * Render contact link for sur_demande variants
   */
  renderContactLink() {
    return `<div class="product-price"><a href="/pages/contact" class="lgn-contact-link">Nous contacter</a></div>`;
  }

  /**
   * Format price in French style (e.g., "89,00 € HT")
   * @param {number} price - Price in euros (decimal)
   * @param {string} suffix - Optional suffix like 'HT' or 'TTC'
   */
  formatPriceFrench(price, suffix = '') {
    const euros = Math.floor(price);
    const cents = Math.round((price - euros) * 100);

    // Thousands separator
    let eurosStr;
    if (euros >= 1000) {
      const thousands = Math.floor(euros / 1000);
      const remainder = euros % 1000;
      eurosStr = `${thousands} ${String(remainder).padStart(3, '0')}`;
    } else {
      eurosStr = `${euros}`;
    }

    const centsStr = String(cents).padStart(2, '0');
    let result = `${eurosStr},${centsStr} €`;
    if (suffix) result += ` ${suffix}`;
    return result;
  }

  /**
   * Extract numeric ID from Shopify GID
   * e.g., "gid://shopify/ProductVariant/12345" -> "12345"
   */
  extractVariantId(gid) {
    if (!gid) return '';
    const match = gid.match(/\/(\d+)$/);
    return match ? match[1] : gid;
  }

  /**
   * Optimize Shopify CDN image URL with size parameter
   */
  optimizeImageUrl(url, width) {
    if (!url) return '';

    // Shopify CDN URL transformation
    // https://cdn.shopify.com/s/files/.../image.jpg -> add _widthx before extension
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/(\.[a-zA-Z]+)(\?.*)?$/, `_${width}x$1$2`);
    }

    return url;
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Render skeleton card for loading state
   */
  static renderSkeleton() {
    return `
      <div class="product-card product-card--skeleton p-3">
        <div class="product-card__image-wrapper position-relative mb-3" data-image-ratio="square">
          <div class="skeleton-box w-100 h-100"></div>
        </div>
        <div class="product-card__content-wrapper py-3">
          <div class="skeleton-text skeleton-text--sm mb-2"></div>
          <div class="skeleton-text skeleton-text--lg mb-2"></div>
          <div class="skeleton-text skeleton-text--sm mb-2"></div>
          <div class="skeleton-text skeleton-text--md"></div>
        </div>
      </div>
    `;
  }

  /**
   * Render multiple skeletons
   */
  static renderSkeletons(count = 9) {
    return Array(count).fill(VariantCard.renderSkeleton()).join('');
  }

  /**
   * Render empty state
   */
  static renderEmpty(message = 'Aucun produit trouvé') {
    return `
      <div class="collection-product-list__products--empty product-list__products--empty py-5 my-5 text-center">
        <h3>${message}</h3>
      </div>
    `;
  }
}

/**
 * Custom element for variant cart form
 * Handles add-to-cart via AJAX
 */
if (!customElements.get('variant-cart-form')) {
  class VariantCartForm extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('button');
      this.form = this.querySelector('form');

      if (this.button) {
        this.button.addEventListener('click', this.handleClick.bind(this));
      }
    }

    async handleClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.button.dataset.disabled === 'true') return;

      this.button.dataset.disabled = 'true';

      try {
        const formData = new FormData(this.form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Add to cart failed');
        }

        const item = await response.json();

        // Trigger cart drawer open if available
        this.openCartDrawer();

        // Dispatch custom event for other components
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { item }
        }));

      } catch (error) {
        console.error('[VariantCartForm] Error:', error);
        // Could show error toast here
      } finally {
        this.button.dataset.disabled = 'false';
      }
    }

    async openCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;

      try {
        // Fetch updated cart drawer section
        const response = await fetch('/?sections=cart-drawer');
        const sections = await response.json();

        // Parse and update cart drawer content
        const parser = new DOMParser();
        const cartDrawerHTML = parser.parseFromString(sections['cart-drawer'], 'text/html');
        const newDrawerInner = cartDrawerHTML.querySelector('.drawer__inner');
        const newCartDrawerElement = cartDrawerHTML.querySelector('cart-drawer');

        if (newDrawerInner) {
          const currentDrawerInner = cartDrawer.querySelector('.drawer__inner');
          if (currentDrawerInner) {
            currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
          }
        }

        // Update is-empty class
        if (newCartDrawerElement && !newCartDrawerElement.classList.contains('is-empty')) {
          cartDrawer.classList.remove('is-empty');
        } else {
          cartDrawer.classList.add('is-empty');
        }

        // Update cart icon count
        this.updateCartIcon();

        // Re-attach overlay event listener (destroyed by innerHTML)
        const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          const newOverlay = overlay.cloneNode(true);
          overlay.parentNode.replaceChild(newOverlay, overlay);
          newOverlay.addEventListener('click', () => cartDrawer.close());
        }

        // Open the drawer
        cartDrawer.open();
      } catch (error) {
        console.error('[VariantCartForm] Error refreshing cart drawer:', error);
        // Fallback: just open the drawer
        cartDrawer.open();
      }
    }

    updateCartIcon() {
      fetch('/?section_id=header-cart-icon')
        .then(res => res.text())
        .then(text => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-header-cart-icon')?.innerHTML;

          if (resultsMarkup) {
            const cartCount = document.querySelector('custom-header .nav-item .cart-count');
            if (cartCount) {
              cartCount.innerHTML = resultsMarkup.trim();
              cartCount.setAttribute('data-hidden', 'false');
            }
          }
        })
        .catch(err => console.error('[VariantCartForm] Error updating cart icon:', err));
    }
  }

  customElements.define('variant-cart-form', VariantCartForm);
}

// Export for use in other modules
window.VariantCard = VariantCard;
